"use client";

import { InitConversationProvider } from "../../components/InitConversationProvider";
import { useInitChatbox } from "../../hooks/useInitChatbox";
import { useConversationStore } from "../../store/conversationStore";
import { useConversationsStore } from "../../store/conversationsStore";
import { Pages, useWebchatStore } from "../store";
import { PageSkeleton } from "@/components/PageSkeleton";
import { Chatbox } from "@/components/chat/Chatbox";
import { useEffect, useState } from "react";

export function WebchatPage() {
  const {
    use: { deployment, conversationUid },
  } = useConversationsStore();

  if (!deployment || !conversationUid) {
    return <PageSkeleton />;
  }

  return (
    <InitConversationProvider conversationUid={conversationUid}>
      <ChatPage />
    </InitConversationProvider>
  );
}

function ChatPage() {
  const chatbox = useInitChatbox();

  const { store } = chatbox;

  const {
    bind: { sendMessage },
  } = store;

  const {
    use: { pendingVisitorMessage },
    bind: { setPendingVisitorMessage },
  } = useConversationsStore();

  const {
    use: { conversation },
  } = useConversationStore();

  useMonitorUnreadMessageCount(chatbox);

  useEffect(() => {
    if (!conversation?.uid) {
      return;
    }
    if (pendingVisitorMessage) {
      const message = pendingVisitorMessage;
      setPendingVisitorMessage("");
      void sendMessage(message);
    }
  }, [pendingVisitorMessage, conversation]);

  return (
    <div className="flex h-full justify-center overflow-hidden">
      <Chatbox className="chatbox h-full grow" store={store} />
    </div>
  );
}

const useMonitorUnreadMessageCount = (
  chatbox: ReturnType<typeof useInitChatbox>
) => {
  const {
    use: { page, visible: toggleVisible },
    bind: { setUnreadMessageCount },
  } = useWebchatStore();

  const {
    use: { visibleMessages },
  } = chatbox.store;

  const inChatPage = page === Pages.CHAT && toggleVisible;
  const [readMessageCount, setReadMessageCount] = useState(0);

  useEffect(() => {
    if (!inChatPage) {
      setReadMessageCount((prev) => {
        if (prev === 0) {
          return visibleMessages.length;
        }
        return prev;
      });
      return;
    }
    setReadMessageCount(visibleMessages.length);
    setUnreadMessageCount(0);
  }, [visibleMessages, inChatPage, setUnreadMessageCount, setReadMessageCount]);

  useEffect(() => {
    if (inChatPage) {
      return;
    }
    const unreadCount = visibleMessages.length - readMessageCount;
    setUnreadMessageCount(unreadCount > 0 ? unreadCount : 0);
  }, [visibleMessages, inChatPage, readMessageCount, setUnreadMessageCount]);
};
